import { useState, useMemo, useRef } from 'react'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Button } from '../../lib/shadcn/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../lib/shadcn/command'
import { Popover, PopoverTrigger } from '../../lib/shadcn/popover'
import { cn } from '../../lib/shadcn/utils'
import { Check, ChevronsUpDown, Plus } from 'lucide-react'

export interface IngredientOption {
  id: string
  name: string
}

interface IngredientComboboxProps {
  ingredients: IngredientOption[]
  value: string
  onChange: (id: string) => void
  /** Create a new ingredient by name; resolve to its new id. */
  onCreate?: (name: string) => Promise<string | undefined>
  excludeIds?: string[]
  placeholder?: string
  disabled?: boolean
  className?: string
}

export default function IngredientCombobox({
  ingredients,
  value,
  onChange,
  onCreate,
  excludeIds = [],
  placeholder = 'Select ingredient…',
  disabled = false,
  className,
}: IngredientComboboxProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  // When the picker sits inside a dialog (e.g. the recipe editor), render the
  // dropdown inside that dialog. The dialog and popover ship different Radix
  // focus-scope copies, so a dropdown portaled to <body> counts as "outside"
  // the dialog and its focus trap pulls focus off the search box (issue #40).
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [container, setContainer] = useState<HTMLElement | null>(null)

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setContainer(triggerRef.current?.closest<HTMLElement>('[role="dialog"]') ?? null)
    }
    setOpen(next)
  }

  const selected = ingredients.find(i => i.id === value)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return ingredients
      .filter(i => !excludeIds.includes(i.id))
      .filter(i => (q ? i.name.toLowerCase().includes(q) : true))
  }, [ingredients, excludeIds, search])

  const exactMatch = ingredients.some(
    i => i.name.trim().toLowerCase() === search.trim().toLowerCase(),
  )
  const canCreate = !!onCreate && search.trim().length > 0 && !exactMatch

  const handleCreate = async () => {
    if (!onCreate) return
    setCreating(true)
    try {
      const id = await onCreate(search.trim())
      if (id) {
        onChange(id)
        setSearch('')
        setOpen(false)
      }
    } finally {
      setCreating(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('justify-between font-normal', className)}
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {selected ? selected.name : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverPrimitive.Portal container={container}>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          collisionBoundary={container ? [container] : []}
          collisionPadding={8}
          className="z-50 w-[--radix-popover-trigger-width] rounded-md border bg-popover p-0 text-popover-foreground shadow-retool-md outline-none pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or type to create…"
              value={search}
              onValueChange={setSearch}
            />
            <CommandList
              className="max-h-64"
              style={{
                maxHeight:
                  'min(16rem, calc(var(--radix-popover-content-available-height, 16rem) - 2.75rem))',
              }}
            >
              {filtered.length === 0 && !canCreate ? (
                <CommandEmpty>No ingredients found.</CommandEmpty>
              ) : null}
              {filtered.length > 0 && (
                <CommandGroup>
                  {filtered.map(option => (
                    <CommandItem
                      key={option.id}
                      value={option.id}
                      onSelect={() => {
                        onChange(option.id)
                        setSearch('')
                        setOpen(false)
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === option.id ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      {option.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {canCreate && (
                <CommandGroup>
                  <CommandItem
                    value={`__create__${search}`}
                    disabled={creating}
                    onSelect={() => void handleCreate()}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {creating ? 'Creating…' : `Create “${search.trim()}”`}
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </Popover>
  )
}
